import { DEFAULT_LOCALE } from 'constants/index';

export const TURKISH_LOCALE = 'tr-TR';

import some from 'lodash/some';
import { Children, isValidElement } from 'react';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';
export const rtlLocales = ['ar'];

// FYI this type name is used in a Support RegEx to extract the locales
export type SupportedLocalesKeys =
  | 'en'
  | 'zh-CN'
  | 'es-ES'
  | 'ko-KR'
  | 'ja-JP'
  | 'fr-FR'
  | 'it-IT'
  | 'pt-PT'
  | 'de-DE'
  | 'id-ID'
  | 'vi-VN'
  | 'ar-SA'
  | 'tr-TR';

export type Translations = Record<string, string | Record<string, string>>;
type Locales = { [key in SupportedLocalesKeys]: string };

const languageLoaders: Record<
  SupportedLocalesKeys,
  () => Promise<Translations>
> = {
  en: () =>
    import(/* webpackChunkName: "translations-EN" */ './locales/EN.json'),
  'zh-CN': () =>
    import(/* webpackChunkName: "translations-zh-CN" */ './locales/zh-CN.json'),
  'es-ES': () =>
    import(/* webpackChunkName: "translations-es-ES" */ './locales/es-ES.json'),
  'ko-KR': () =>
    import(/* webpackChunkName: "translations-ko-KR" */ './locales/ko-KR.json'),
  'ja-JP': () =>
    import(/* webpackChunkName: "translations-ja-JP" */ './locales/ja-JP.json'),
  'fr-FR': () =>
    import(/* webpackChunkName: "translations-fr-FR" */ './locales/fr-FR.json'),
  'it-IT': () =>
    import(/* webpackChunkName: "translations-it-IT" */ './locales/it-IT.json'),
  'pt-PT': () =>
    import(/* webpackChunkName: "translations-pt-PT" */ './locales/pt-PT.json'),
  'de-DE': () =>
    import(/* webpackChunkName: "translations-de-DE" */ './locales/de-DE.json'),
  'id-ID': () =>
    import(/* webpackChunkName: "translations-id-ID" */ './locales/id-ID.json'),
  'vi-VN': () =>
    import(/* webpackChunkName: "translations-vi-VN" */ './locales/vi-VN.json'),
  'ar-SA': () =>
    import(/* webpackChunkName: "translations-ar-SA" */ './locales/ar-SA.json'),
  'tr-TR': () =>
    import(/* webpackChunkName: "translations-tr-TR" */ './locales/tr-TR.json'),
};

export const loadLanguageFile = async (
  language: SupportedLocalesKeys,
): Promise<Translations> => {
  const loader = languageLoaders[language];
  if (!loader) {
    throw new Error(`No loader found for language: ${language}`);
  }
  return loader();
};

export const locales: Locales = {
  en: 'EN',
  'zh-CN': 'zh-CN',
  'es-ES': 'es-ES',
  'ko-KR': 'ko-KR',
  'ja-JP': 'ja-JP',
  'fr-FR': 'fr-FR',
  'it-IT': 'it-IT',
  'pt-PT': 'pt-PT',
  'de-DE': 'de-DE',
  'id-ID': 'id-ID',
  'vi-VN': 'vi-VN',
  'ar-SA': 'ar-SA',
  [TURKISH_LOCALE]: 'tr-TR',
};

export const cmsLocales = [
  'en',
  'zh-CN',
  'es-ES',
  'ko-KR',
  'ja-JP',
  'fr-FR',
  'pt-PT',
];

export const localeCodes = Object.keys(locales);
export const extractLocaleCode = (locale: string) => locale.split('-')[0];
export const isRtlLocale = (locale: string) =>
  rtlLocales.includes(extractLocaleCode(locale));

import defaultTranslationsJson from './locales/EN.json';

const defaultTranslations: Translations = defaultTranslationsJson;

const i18nInstances = new Map<SupportedLocalesKeys, I18n>();
const translationCache = new Map<SupportedLocalesKeys, Translations>();
const loadingPromises = new Map<SupportedLocalesKeys, Promise<Translations>>();

export const normalizeLocale = (locale: string): SupportedLocalesKeys => {
  if (!locale || typeof locale !== 'string') {
    return 'en';
  }
  const normalized = locale.toLowerCase().replace(/[_-].*$/, '');

  const localeMap: Record<string, SupportedLocalesKeys> = {
    en: 'en',
    es: 'es-ES',
    fr: 'fr-FR',
    it: 'it-IT',
    pt: 'pt-PT',
    de: 'de-DE',
    zh: 'zh-CN',
    ko: 'ko-KR',
    ja: 'ja-JP',
    id: 'id-ID',
    vi: 'vi-VN',
    ar: 'ar-SA',
    tr: 'tr-TR',
  };

  return localeMap[normalized] || 'en';
};

export const getI18nInstance = (locale: string): I18n => {
  const normalizedLocale = normalizeLocale(locale);
  if (!i18nInstances.has(normalizedLocale)) {
    i18nInstances.set(normalizedLocale, new I18n(normalizedLocale));
  }
  return i18nInstances.get(normalizedLocale)!;
};

export class I18n {
  currentLocale: SupportedLocalesKeys;
  strings: Translations;
  private updateCallbacks: Set<() => void> = new Set();

  constructor(locale: SupportedLocalesKeys) {
    this.currentLocale = locale;
    this.strings = defaultTranslations;
    this.loadTranslations();
  }

  onUpdate(callback: () => void) {
    this.updateCallbacks.add(callback);
    return () => this.updateCallbacks.delete(callback);
  }

  isLoaded(): boolean {
    return (
      translationCache.has(this.currentLocale) ||
      this.strings !== defaultTranslations
    );
  }

  private notifyUpdates() {
    this.updateCallbacks.forEach(callback => callback());
  }

  private async loadTranslations() {
    if (translationCache.has(this.currentLocale)) {
      this.strings = translationCache.get(this.currentLocale)!;
      this.notifyUpdates();
      return;
    }

    if (loadingPromises.has(this.currentLocale)) {
      try {
        this.strings = await loadingPromises.get(this.currentLocale)!;
        this.notifyUpdates();
      } catch (error) {
        console.error(
          `Failed to load translations for locale ${this.currentLocale}:`,
          error,
        );
      }
      return;
    }

    const loadingPromise = loadLanguageFile(this.currentLocale);
    loadingPromises.set(this.currentLocale, loadingPromise);

    try {
      this.strings = await loadingPromise;
      translationCache.set(this.currentLocale, this.strings);
      this.notifyUpdates();
    } catch (error) {
      console.error(
        `Failed to load translations for locale ${this.currentLocale}:`,
        error,
      );
    } finally {
      loadingPromises.delete(this.currentLocale);
    }
  }

  get localeCode() {
    return extractLocaleCode(this.currentLocale);
  }

  get isRtl() {
    return isRtlLocale(this.currentLocale);
  }

  t(
    key: string,
    opts: {
      [key: string]: string;
    } & {
      count?: number;
    } = {},
  ) {
    try {
      const { count } = opts;
      const string = this.getString(key, count);
      const optionsRegex = /%{([a-zA-Z0-9]+)}/g;
      const hasReactValues = some(Object.values(opts), o => isValidElement(o));

      if (hasReactValues) {
        const chunks = string.split(optionsRegex).map((chunk: string) => {
          return opts[chunk] || chunk;
        });

        return Children.toArray(chunks).filter(el => !!el);
      }

      return string.replace(optionsRegex, (_str, match) => opts[match] || '');
    } catch (error) {
      errorNotifier.notify(error, { key });
    }
  }

  getString(key: string, count?: number): string {
    if (typeof count === 'number') {
      key += count === 0 ? '.zero' : count === 1 ? '.one' : '.other';
    }

    let str = this.strings[key];

    if (!str && this.currentLocale !== 'en') {
      str = defaultTranslations[key];
    }

    str = str || key;

    return typeof str === 'string' ? str : str[0];
  }

  static isValid(locale: string) {
    return Object.keys(locales).includes(locale);
  }
}

const getBrowserLanguage = () => {
  const navigator = global.navigator;
  return (
    (navigator.languages && navigator.languages[0]) ||
    navigator.language ||
    navigator.userLanguage
  );
};

export const getDefaultSupportedLocale = () => {
  try {
    const matchLocale = (locale: string) =>
      extractLocaleCode(locale) === extractLocaleCode(getBrowserLanguage());
    const supported = Object.keys(locales).find(matchLocale);

    return Intl.getCanonicalLocales(supported)[0] || DEFAULT_LOCALE;
  } catch (_e) {
    return DEFAULT_LOCALE;
  }
};
